import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../../services/ApiService";
import ShareModal from "../../components/ShareModal";

/* ── Logo ──────────────────────────────────────────────────── */
const Logo = ({ size = 38 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 140 140" fill="none">
    <defs>
      <linearGradient id="bd-ng" x1="30" y1="15" x2="110" y2="125">
        <stop offset="0%" stopColor="#00FFE0" /><stop offset="50%" stopColor="#00E5CC" /><stop offset="100%" stopColor="#22D97F" />
      </linearGradient>
    </defs>
    <rect width="140" height="140" rx="30" fill="#0F2140" />
    <path d="M70 18L28 37v32c0 26 42 45 42 45s42-19 42-45V37L70 18z" fill="none" stroke="url(#bd-ng)" strokeWidth="4" />
    <circle cx="70" cy="60" r="16" fill="none" stroke="url(#bd-ng)" strokeWidth="2.5" opacity="0.35" />
    <text x="70" y="68" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="800" fontSize="20" fill="url(#bd-ng)">J</text>
  </svg>
);

const Ico = {
  arrowLeft: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  arrowRight: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>,
  share: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.59 13.51l6.83 3.98" /><path d="M15.41 6.51l-6.82 3.98" /></svg>,
};

interface Blog {
  id?: string; _id?: string; slug: string; title: string; content: string;
  coverImage?: string; read_time?: string; excerpt?: string; createdAt?: string;
  author?: string | { name: string }; category?: string; tags?: string[];
}

export default function BlogPublicDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [related, setRelated] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  const getAuthorName = (author: any): string => {
    if (!author) return "Unknown";
    if (typeof author === "string") return author;
    return author.name || "Unknown";
  };

  const getMediaUrl = (url?: string | null): string => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const base = process.env.REACT_APP_MEDIA_BASE || "";
    if (url.startsWith("/uploads/")) return `${base}${url}`;
    return `${base}/uploads/blogs/${url}`;
  };

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const res: any = await api.get(`/public/blogs/${slug}`);
        const blogData = res.blog || res.data?.blog || res.data || null;
        if (!blogData || blogData.success === false) { setError("Blog not found"); return; }
        setBlog(blogData);

        const listRes: any = await api.get("/public/blogs");
        const all: Blog[] = listRes.blogs ?? listRes.data?.blogs ?? (Array.isArray(listRes.data) ? listRes.data : []);
        setRelated(all.filter(b => b.slug !== slug).slice(0, 3));
      } catch (err) {
        setError("Failed to load blog post");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  if (loading) return (
    <div className="bd-root"><style dangerouslySetInnerHTML={{ __html: BD_STYLES }} /><div className="bd-msg">Loading...</div></div>
  );
  if (error || !blog) return (
    <div className="bd-root"><style dangerouslySetInnerHTML={{ __html: BD_STYLES }} /><div className="bd-msg error">{error || "Blog not found"}</div></div>
  );

  return (
    <div className="bd-root">
      <style dangerouslySetInnerHTML={{ __html: BD_STYLES }} />

      {/* NAV */}
      <nav className="bd-nav">
        <a href="#" className="bd-nav-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <Logo size={36} /><span className="bd-wm"><span className="jep">Jep</span><span className="tags">tags</span></span>
        </a>
        <button className="bd-back" onClick={() => navigate('/blogs')}>
          <span className="bd-ico-16">{Ico.arrowLeft}</span> All Posts
        </button>
      </nav>

      {/* ARTICLE */}
      <article className="bd-article">

        {blog.category && <span className="bd-cat-badge">{blog.category}</span>}
        <h1>{blog.title}</h1>

        {/* META */}
        <div className="bd-meta">
          <span className="bd-author">{getAuthorName(blog.author)}</span>
          {blog.createdAt && (
            <span className="bd-meta-item"><span className="bd-ico-14">{Ico.calendar}</span>{new Date(blog.createdAt).toLocaleDateString()}</span>
          )}
          {blog.read_time && (
            <span className="bd-meta-item"><span className="bd-ico-14">{Ico.clock}</span>{blog.read_time}</span>
          )}
          <button className="bd-share-btn" onClick={() => setShareOpen(true)}>
            <span className="bd-ico-16">{Ico.share}</span>
          </button>
        </div>

        {/* COVER */}
        {blog.coverImage && (
          <img src={getMediaUrl(blog.coverImage)} className="bd-cover" alt={blog.title} />
        )}

        {/* CONTENT */}
        <div className="bd-content" dangerouslySetInnerHTML={{ __html: blog.content }} />

        {/* TAGS */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="bd-tags">
            {blog.tags.map(tag => (
              <span key={tag} className="bd-tag">#{tag}</span>
            ))}
          </div>
        )}
      </article>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bd-related">
          <h2>Related <span className="grad">Articles</span></h2>
          <div className="bd-related-grid">
            {related.map(r => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="bd-rcard">
                {r.coverImage ? (
                  <img src={getMediaUrl(r.coverImage)} className="bd-rcard-img" alt={r.title} />
                ) : (
                  <div className="bd-rcard-ph"><span>📝</span></div>
                )}
                <div className="bd-rcard-body">
                  <h3>{r.title}</h3>
                  <span className="bd-rcard-read">{r.read_time || "5 min read"}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* SHARE MODAL */}
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} url={window.location.href} title={blog.title} />

      {/* FLOAT SHARE */}
      <button className="bd-float-share" onClick={() => setShareOpen(true)}>
        {Ico.share}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
const BD_STYLES = `
.bd-root *,.bd-root *::before,.bd-root *::after{margin:0;padding:0;box-sizing:border-box}
.bd-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;overflow-x:hidden;min-height:100vh;padding-bottom:100px;
}
.bd-root a{text-decoration:none;color:inherit}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.bd-ico-16 svg{width:16px;height:16px}.bd-ico-14 svg{width:14px;height:14px}

/* NAV */
.bd-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:16px 40px;display:flex;align-items:center;justify-content:space-between;backdrop-filter:blur(20px);background:rgba(3,10,20,0.85);border-bottom:1px solid rgba(0,229,204,0.06)}
.bd-nav-logo{display:flex;align-items:center;gap:12px;text-decoration:none;cursor:pointer}
.bd-wm{font-size:22px;font-weight:700;letter-spacing:-.5px}
.bd-back{display:flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.bd-back:hover{border-color:var(--cyan);color:var(--cyan)}

/* MSG */
.bd-msg{text-align:center;padding:140px 24px 60px;color:rgba(224,240,240,0.4);font-size:16px}
.bd-msg.error{color:#f87171}

/* ARTICLE */
.bd-article{max-width:800px;margin:0 auto;padding:120px 24px 0}

.bd-cat-badge{display:inline-block;padding:6px 16px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:11px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:var(--cyan);margin-bottom:16px}

.bd-article h1{font-size:clamp(28px,5vw,48px);font-weight:800;line-height:1.15;letter-spacing:-1.5px;margin-bottom:20px}

/* META */
.bd-meta{display:flex;align-items:center;gap:20px;flex-wrap:wrap;margin-bottom:32px;font-size:13px;color:rgba(224,240,240,0.4)}
.bd-author{font-weight:600;color:rgba(224,240,240,0.6)}
.bd-meta-item{display:flex;align-items:center;gap:6px}
.bd-share-btn{margin-left:auto;background:none;border:1px solid rgba(0,229,204,0.15);border-radius:8px;padding:8px;cursor:pointer;color:rgba(224,240,240,0.4);transition:all .3s;display:flex}
.bd-share-btn:hover{border-color:var(--cyan);color:var(--cyan)}

/* COVER */
.bd-cover{width:100%;max-height:520px;object-fit:cover;border-radius:20px;border:1px solid rgba(0,229,204,0.06);margin-bottom:40px;box-shadow:0 20px 60px rgba(0,0,0,0.3)}

/* CONTENT — prose styling for dark theme */
.bd-content{font-size:16px;line-height:1.8;color:rgba(224,240,240,0.65)}
.bd-content h1,.bd-content h2,.bd-content h3,.bd-content h4{color:#e0f0f0;font-weight:700;margin-top:32px;margin-bottom:12px}
.bd-content h2{font-size:24px}.bd-content h3{font-size:20px}
.bd-content p{margin-bottom:16px}
.bd-content a{color:var(--cyan);text-decoration:underline;text-underline-offset:3px}
.bd-content a:hover{color:var(--cyan-bright)}
.bd-content ul,.bd-content ol{padding-left:24px;margin-bottom:16px}
.bd-content li{margin-bottom:8px}
.bd-content blockquote{border-left:3px solid var(--cyan);padding:16px 20px;margin:24px 0;background:rgba(0,229,204,0.04);border-radius:0 12px 12px 0;font-style:italic;color:rgba(224,240,240,0.5)}
.bd-content img{max-width:100%;border-radius:12px;margin:24px 0}
.bd-content code{background:rgba(0,229,204,0.08);padding:2px 6px;border-radius:4px;font-size:14px;font-family:'JetBrains Mono',monospace}
.bd-content pre{background:rgba(15,33,64,0.8);border:1px solid rgba(0,229,204,0.08);border-radius:12px;padding:20px;overflow-x:auto;margin:24px 0}
.bd-content pre code{background:none;padding:0}
.bd-content table{width:100%;border-collapse:collapse;margin:24px 0}
.bd-content th,.bd-content td{padding:10px 14px;border:1px solid rgba(0,229,204,0.08);text-align:left;font-size:14px}
.bd-content th{background:rgba(0,229,204,0.06);font-weight:700}

/* TAGS */
.bd-tags{display:flex;flex-wrap:wrap;gap:10px;margin-top:40px}
.bd-tag{padding:6px 16px;border-radius:100px;background:rgba(0,229,204,0.06);border:1px solid rgba(0,229,204,0.1);color:rgba(224,240,240,0.5);font-size:13px}

/* RELATED */
.bd-related{max-width:1100px;margin:80px auto 0;padding:0 24px}
.bd-related h2{font-size:28px;font-weight:800;margin-bottom:28px}
.bd-related-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}

.bd-rcard{border-radius:16px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);overflow:hidden;transition:all .4s}
.bd-rcard:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px)}
.bd-rcard-img{width:100%;height:180px;object-fit:cover}
.bd-rcard-ph{height:180px;background:rgba(0,229,204,0.04);display:flex;align-items:center;justify-content:center;font-size:32px}
.bd-rcard-body{padding:20px}
.bd-rcard-body h3{font-size:16px;font-weight:700;line-height:1.4;margin-bottom:8px}
.bd-rcard-read{font-size:12px;color:rgba(224,240,240,0.3)}

/* FLOAT SHARE */
.bd-float-share{position:fixed;bottom:28px;right:28px;width:52px;height:52px;border-radius:50%;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 8px 32px rgba(0,229,204,0.35);z-index:150;transition:transform .3s}
.bd-float-share:hover{transform:scale(1.1)}
.bd-float-share svg{width:22px;height:22px;color:var(--navy-deep);stroke:var(--navy-deep)}

/* RESPONSIVE */
@media(max-width:900px){
  .bd-nav{padding:14px 20px}
  .bd-article{padding:100px 20px 0}
  .bd-related-grid{grid-template-columns:1fr 1fr}
}
@media(max-width:600px){
  .bd-related-grid{grid-template-columns:1fr}
}
`;
